import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { DEFAULT_ISLAND_KEY, getIslandUrl } from 'alakai-aloha-shared';
import AppLink from '../components/shared/AppLink';
import { useApexOnlyPage } from '../hooks/useApexOnlyPage';
import { sanityClient } from '../lib/sanity';
import { aboutQueries } from '../lib/sanityQueries';
import { urlFor } from '../lib/sanityImage';
import { buildApexCanonicalUrl } from '../lib/siteUrls';
import { trackEvent } from '../utils/analytics';

const MANIFESTO_PARAGRAPHS = [
  {
    key: 'turning-point',
    content:
      'We are living through an epic turning point. AI is moving into everything: what we watch, what we buy, how we work, and how we decide.',
  },
  {
    key: 'magic',
    content:
      'AI can feel a bit like magic sometimes, and like all good magic, it can also feel unsettling.',
  },
  {
    key: 'natural-world',
    content: 'As our digital lives become increasingly infinite, the natural world becomes more precious.',
  },
  {
    key: 'kauai-full-volume',
    content: 'Kauai is the natural world at full volume. Ridgelines. Waterfalls. Reef. Rain. Light. Culture.',
  },
  {
    key: 'experiential-proof',
    content: (
      <>
        Nothing in your feed can replicate the feeling of standing at the edge of the{' '}
        <AppLink
          to={getIslandUrl(DEFAULT_ISLAND_KEY, '/activities/awaawapuhi-trail')}
          className="text-emerald-200 underline hover:text-emerald-100"
        >
          Awaʻawapuhi Trail
        </AppLink>{' '}
        and drinking in{' '}
        <AppLink
          to={getIslandUrl(DEFAULT_ISLAND_KEY, '/guides/na-pali-coast-tours-overview')}
          className="text-emerald-200 underline hover:text-emerald-100"
        >
          Nā Pali
        </AppLink>
        . No virtual world can replicate the thrill of a{' '}
        <AppLink
          to={getIslandUrl(DEFAULT_ISLAND_KEY, '/blog/honu-of-kauai-reef-guardians-cultural-icons')}
          className="text-emerald-200 underline hover:text-emerald-100"
        >
          honu
        </AppLink>{' '}
        gliding up to you as you snorkel. Nothing on your screen can equal the feeling of whales beneath your boat
        or dolphins beneath your board.
      </>
    ),
  },
  {
    key: 'intersection',
    content:
      'Alakaʻi Aloha exists at this intersection: built with AI, grounded in place, focused on helping you spend limited vacation days and dollars in the most extraordinary way possible.',
  },
];

const FOUNDER_NOTE_PARAGRAPHS = [
  'Alakaʻi Aloha started as a sandbox: a real company built to test emerging AI capability under real operating pressure. I built it by orchestrating a fleet of specialized AI agents across research, content, engineering, and operations. In doing so, I gained a glimpse of where knowledge work is headed.',
  'The team below reflects a new operating model: one human founder, supported by AI agents with clear responsibilities, dedicated workflows, and measurable output. These agents are not digital replicas of human roles, and they are not arranged in a traditional functional org chart. We organize around recurring business problems and design for controlled autonomy, not human-in-the-loop bottlenecks on every output.',
  'This is not a gimmick. It is a preview of how high-performance companies will be built and rebuilt: smaller human cores, larger agentic capability, faster execution, and higher quality.',
  "If this site helps you plan an unforgettable trip, I'll be very happy. If it expands your view of what modern work can look like and you want to talk, please don't hesitate to reach out.",
];

const About = () => {
  const isRedirecting = useApexOnlyPage('/about');
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [employeesError, setEmployeesError] = useState(null);

  useEffect(() => {
    let isCancelled = false;
    trackEvent('about_page_view', { source: 'about_page' });

    const fetchEmployees = async () => {
      setLoadingEmployees(true);
      try {
        const result = await sanityClient.fetch(aboutQueries.getActiveEmployees);
        if (!isCancelled) {
          setEmployees(Array.isArray(result) ? result : []);
          setEmployeesError(null);
        }
      } catch (error) {
        console.error('Error fetching active employees:', error);
        if (!isCancelled) {
          setEmployees([]);
          setEmployeesError(error);
        }
      } finally {
        if (!isCancelled) {
          setLoadingEmployees(false);
        }
      }
    };

    fetchEmployees();
    return () => {
      isCancelled = true;
    };
  }, []);

  const showTeamSection = useMemo(() => {
    if (loadingEmployees) return true;
    if (employeesError) return true;
    return employees.length > 0;
  }, [loadingEmployees, employeesError, employees.length]);

  const handleContactClick = (employee, channel) => {
    trackEvent('about_contact_click', {
      source: 'about_page',
      employee_id: employee?._id,
      employee_name: employee?.name,
      channel,
    });
  };

  if (isRedirecting) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>About Alakaʻi Aloha | Why We Built It</title>
        <meta
          name="description"
          content="Read the Alakaʻi Aloha thesis, meet the team, and see how an AI-native company is building island-specific Hawaii travel guidance and planning tools."
        />
        <link rel="canonical" href={buildApexCanonicalUrl('/about')} />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 pb-6">
        <section className="pt-0 text-left">
          <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-white">About Us: Why We Built Alakaʻi Aloha.</h1>
          <p className="mt-2 italic text-white/90 max-w-4xl leading-relaxed">
            Built with AI. Grounded in place. Focused on helping travelers experience Hawaiʻi in ways that are deeply
            personal, practical, and unforgettable.
          </p>
        </section>

        <section className="mt-12">
          <h2 className="text-2xl md:text-3xl font-semibold text-emerald-200">The Alakaʻi Aloha Thesis</h2>
          <div className="mt-5 glass-card rounded-lg p-6 md:p-8">
            {MANIFESTO_PARAGRAPHS.map((paragraph, index) => (
              <p key={paragraph.key} className={`text-white/90 leading-relaxed ${index > 0 ? 'mt-4' : ''}`}>
                {paragraph.content}
              </p>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-2xl md:text-3xl font-semibold text-emerald-200">Founder&apos;s Note</h2>
          <div className="mt-5 glass-card rounded-lg p-6">
            {FOUNDER_NOTE_PARAGRAPHS.map((paragraph, index) => (
              <p key={paragraph} className={`text-white/90 leading-relaxed ${index > 0 ? 'mt-4' : ''}`}>
                {paragraph}
              </p>
            ))}
          </div>
        </section>

        {showTeamSection ? (
          <section className="mt-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-white">The Team</h2>
            {loadingEmployees ? (
              <p className="mt-4 text-white/80">Loading team profiles...</p>
            ) : null}

            {!loadingEmployees && employeesError ? (
              <div className="mt-4 rounded-lg border border-white/15 bg-white/5 p-5 text-white/90">
                Team profiles are temporarily unavailable. Please try again shortly.
              </div>
            ) : null}

            {!loadingEmployees && !employeesError && employees.length > 0 ? (
              <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {employees.map((employee) => {
                  const hasHeadshotAsset = Boolean(employee?.headshot?.asset);
                  return (
                    <article key={employee._id} className="glass-card rounded-lg overflow-hidden h-full flex flex-col">
                      {hasHeadshotAsset ? (
                        <div className="relative">
                          <img
                            src={urlFor(employee.headshot)
                              .width(900)
                              .height(900)
                              .fit('crop')
                              .auto('format')
                              .quality(80)
                              .url()}
                            alt={employee?.headshot?.alt || `${employee.name} headshot`}
                            className="w-full h-60 object-cover"
                            loading="lazy"
                          />
                          {employee.isVirtual ? (
                            <div className="pointer-events-none absolute top-0 right-0 h-20 w-20 overflow-hidden">
                              <span className="absolute top-3 -right-7 w-28 rotate-45 border-y border-white/20 bg-black/55 py-0.5 text-center text-[10px] font-semibold tracking-[0.05em] text-white/80">
                                AI Agent
                              </span>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      <div className="p-5 flex flex-1 flex-col">
                        <h3 className="text-2xl font-semibold text-white">{employee.name}</h3>

                        <p className="mt-2 text-sm font-medium text-emerald-200">{employee.roleTitle}</p>
                        {employee.roleShortDescription ? (
                          <p className="mt-2 text-sm text-white/90">{employee.roleShortDescription}</p>
                        ) : null}

                        {employee.shortBio ? (
                          <p className="mt-4 text-sm text-white/90 leading-relaxed">{employee.shortBio}</p>
                        ) : employee.bio ? (
                          <p className="mt-4 text-sm text-white/90 leading-relaxed">{employee.bio}</p>
                        ) : null}

                        {employee.topKauaiMoment ? (
                          <div className="mt-4 rounded-lg border border-white/10 bg-white/5 p-3">
                            <p className="text-xs uppercase tracking-wide text-emerald-200">Top Kauai Moment</p>
                            <p className="mt-1 text-sm text-white/90">{employee.topKauaiMoment}</p>
                          </div>
                        ) : null}

                        {employee.externalEmail ? (
                          <div className="mt-auto pt-4">
                            <a
                              href={`mailto:${employee.externalEmail}`}
                              className="inline-block text-sm text-green-300 underline hover:text-green-200"
                              onClick={() => handleContactClick(employee, 'email')}
                            >
                              {employee.externalEmail}
                            </a>
                          </div>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : null}
          </section>
        ) : null}
      </div>
    </>
  );
};

export default About;
